import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldX, Building2, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusPill = ({ ok, expired }) => {
  if (ok) {
    return (
      <div data-testid="verify-badge-ok" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-800 text-xs font-semibold uppercase tracking-widest">
        <ShieldCheck className="w-4 h-4" /> Credenciamento válido
      </div>
    );
  }
  if (expired) {
    return (
      <div data-testid="verify-badge-expired" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold uppercase tracking-widest">
        <ShieldAlert className="w-4 h-4" /> Certificado expirado
      </div>
    );
  }
  return (
    <div data-testid="verify-badge-invalid" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-800 text-xs font-semibold uppercase tracking-widest">
      <ShieldX className="w-4 h-4" /> Credenciamento inativo
    </div>
  );
};

const fmt = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("pt-BR"); } catch { return v; }
};

export default function PublicVerify() {
  const { certNum } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/verificar/${encodeURIComponent(certNum)}`);
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.status === 404
          ? "Nenhum certificado encontrado com este número."
          : "Erro ao consultar o certificado.");
      } finally { setLoading(false); }
    })();
  }, [certNum]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Verificação Pública RAF Direct</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">Certificado nº {certNum}</h1>
        <p className="text-sm text-slate-600 mt-2">
          Página oficial de verificação. Use o QR code do certificado impresso para chegar direto aqui.
        </p>
      </div>

      {loading && <div data-testid="verify-loading" className="text-slate-500">Verificando…</div>}

      {err && (
        <div data-testid="verify-error" className="p-6 border border-red-200 bg-red-50 rounded-xl text-red-800">
          <div className="flex items-center gap-2 font-semibold"><ShieldX className="w-5 h-5" /> Certificado não encontrado</div>
          <p className="text-sm mt-1">{err}</p>
          <Link to="/unidades" className="text-sm underline mt-3 inline-block">Ver todas as Unidades Parceiras</Link>
        </div>
      )}

      {data && (
        <div data-testid="verify-card" className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              {data.clinic.logo_url ? (
                <img src={data.clinic.logo_url} alt="" className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-slate-500" strokeWidth={1.6} />
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Unidade Parceira</div>
                <h2 data-testid="verify-clinic-name" className="font-display font-bold text-2xl text-[#0B2B62] mt-0.5">{data.clinic.name}</h2>
                <div className="text-sm text-slate-600 mt-1">{data.clinic.city}/{data.clinic.state}</div>
              </div>
            </div>
            <StatusPill ok={data.verified} expired={data.expired} />
          </div>

          <div className="p-6 grid sm:grid-cols-3 gap-4 text-sm">
            <Info label="Nº do Certificado" value={data.clinic.certification_number || "—"} testid="verify-cert-num" />
            <Info label="Emitido em" value={fmt(data.clinic.certified_at)} testid="verify-cert-issued" />
            <Info label="Válido até" value={fmt(data.clinic.certification_expires_at)} testid="verify-cert-expires" />
          </div>

          <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-slate-600 max-w-md">
              Este selo garante que a unidade cumpre os critérios técnicos, éticos e assistenciais para acolhimento — auditados pela RAF Direct.
            </p>
            {data.clinic.id && (
              <Link
                data-testid="verify-view-profile"
                to={`/unidades/${data.clinic.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B2B62] hover:underline"
              >
                Ver perfil público <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ label, value, testid }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
    <div data-testid={testid} className="mt-1 font-display text-lg text-[#0B2B62]">{value}</div>
  </div>
);
